/**
 * Manages communications between the embedded partner app and this application.
 *
 * These communications take the form of postMessages between the wrapping third-party app and the
 * React app, which is embedded into the third-party app as an iFrame.
 *
 * If the third-party app is a native/desktop application, it can also open the react app using webview or similar technology.
 * In that case they mostly use `window.external` methods to communicate with the host app.
 *
 * Some integrations can also inject custom javascript to handle things.
 */

import { CartProduct, SellerCartData, SellerWithLocations } from 'cart'
import { LineType, ProductModel } from 'interfaces'
import { Config } from '../../config/ConfigManager'
import { IntegrationsInfoResponse } from '../../features/partsCatalog/Selections/interfaces'
import {
  formatVehicleDisplay,
  getYMMENumberFromVehicle,
} from '../../helpers/utils'
import { LaborItem } from '../../store/models/LaborModel'
import { SearchResultsResponse } from '../../store/models/SearchModels'
import { VehicleSpecificationItem } from '../../store/models/VehicleSpecification'
import AstPunchOutCatalog, {
  defaultIntegrationInfo,
} from '../../store/partsCatalog/api/AstPunchOutCatalogAPI'
import {
  DeliveryDetail,
  OrderResponse,
  OrderResponseDetail,
} from '../cart/interfaces'
import { CartOnlyCommunications } from './CartOnlyCommunications'
import {
  CheckAvailabilityRequest,
  CheckAvailabilityResponse11,
  CheckAvailabilityResponse12,
} from './CheckAvailability'
import { PartsBasketConverter } from './PartsBasketConverter'
import PartnerCommunicationManager, {
  IBasePartnerCommunicationManager,
} from './partnerCommunicationManager'
import { IPartsBasketV10 } from './types/partsBasket-1.0'
import {
  IPartsBasket11,
  IPartsBasketLine11,
  IPartsBasketSearchInfo,
  IPartsBasketVehicleInfo,
} from './types/partsBasket-1.1'
import { IPartsBasket12 } from './types/partsBasket-1.2'
import {
  PartsBasketAction,
  partsBasketResponseType,
} from './types/partsBasketSpec'
import { searchResultsToPartsBasket } from './utils/localInventoryUtils'
import { embeddedConfigManager } from './EmbeddedConfigManager'
import { store } from 'store'
import { cartSlice, setCartInitialized } from 'libs/cart/src/store/cartStore'
import { GaTrackOption } from 'src/config/analytics/tracking/utils'
import { Vehicle } from 'src/store/models/Vehicles'

export class EmbeddedCommunications {
  private detectedPartsBasketVersion = '1.0'

  private partnerCommunicationManager?: IBasePartnerCommunicationManager

  // The initial basket received from the host application. This can have useful info like action, uid etc.
  public incomingPartsBasket?: IPartsBasketV10 | IPartsBasket11 | IPartsBasket12

  public searchInfo?: IPartsBasketSearchInfo

  public vehicleInfo?: IPartsBasketVehicleInfo

  public integrationInfo: IntegrationsInfoResponse = defaultIntegrationInfo

  get isAstPunchOut(): boolean {
    return !this.integrationInfo.epeFlag
  }

  public init = async (): Promise<void> => {
    let checkAvailabilityRequest: CheckAvailabilityRequest

    PartnerCommunicationManager.initializeCommunicationManager(
      embeddedConfigManager.getPartsBasketCreds().partnerId,
      embeddedConfigManager.isAesServer
    )
    this.partnerCommunicationManager =
      PartnerCommunicationManager.getPartnercommunicationmanager()

    // Fetching the integration info.
    this.integrationInfo = await AstPunchOutCatalog.fetchIntegrationsInfo(
      embeddedConfigManager.getPartsBasketCreds()
    )

    const partsBasket = EmbeddedCommunications.findPayload(
      embeddedConfigManager.partsBasketString
    )

    if (partsBasket) {
      // If an integration is not passing version,
      // assume it as 1.0 as only integrations using the older version won't be passing the version info.
      this.detectedPartsBasketVersion = partsBasket.version || '1.0'

      this.incomingPartsBasket = partsBasket

      const { searchInfo, vehicleInfo } =
        PartsBasketConverter.getPartsBasketInfo(partsBasket)
      this.searchInfo = searchInfo
      this.vehicleInfo = vehicleInfo

      // TODO: Need to revisit the cart only mode once discussions resume with shopware.
      if (
        embeddedConfigManager.partsBasketService === 'checkAvailability' ||
        ('action' in partsBasket &&
          // TODO: check if we are suposed to use `checkAvailabilityRequest` here.
          partsBasket?.action === 'checkAvailabilityResponse')
      ) {
        checkAvailabilityRequest = partsBasket
      }
    }

    // TODO: Need to revisit the cart only mode once discussions resume with shopware.
    if (embeddedConfigManager.isCheckAvailabilityRedirect) {
      Config.setCartOnlyMode()
      this.detectedPartsBasketVersion =
        checkAvailabilityRequest?.version || '1.1'
      CartOnlyCommunications.checkAvailability(
        embeddedConfigManager.getPartsBasketCreds(),
        checkAvailabilityRequest
      )
        .then((resp) => {
          EmbeddedCommunications.handleCartOnlyMessage(resp)
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Error getting checkAvailablity response', e)
        })
        .finally(() => {
          store.dispatch(setCartInitialized())
        })
    }
  }

  getPartsBasketVersion(): string {
    return this.detectedPartsBasketVersion
  }

  /**
   * Translates the response from the API Order endpoint into a flattened
   * format compatible with the Parts Basket specs, and transfers back to
   * the shop application
   * @param cart
   * @param orderResponse
   */
  public transferOrderResponse(
    sellerCartData: SellerCartData,
    orderResponse: OrderResponse
  ): void {
    const lines = []
    let singleOrderDetail: OrderResponseDetail = {} as OrderResponseDetail
    let orderNumber = ''
    let deliveryDetail: DeliveryDetail = {}
    for (const orderDetail of orderResponse?.orderDetails || []) {
      singleOrderDetail = orderDetail
      for (const location of orderDetail?.locations || []) {
        for (const order of location?.orders || []) {
          for (const part of order?.parts || []) {
            const pbPart = PartsBasketConverter.orderRespItemToPBItem(part)
            const line = {
              type: LineType.part,
              ...pbPart,
              locationId: location.locationId,
              locationDescription: location.locationDescription,
              orderConfirmationNbr: order.orderNumber,
            }
            lines.push(line)
          }
          deliveryDetail = order.deliveryDetail
          orderNumber = order.orderNumber
        }
      }
    }

    const transferPB = this.prepareSellerBasketForTransfer(
      lines,
      PartsBasketAction.placeOrderResponse,
      sellerCartData.vehicles[0].vehicle
    )
    transferPB.orderInfo = {
      deliveryMethod: deliveryDetail?.deliveryMethod ?? 'Regular',
      orderMessage: singleOrderDetail?.comment ?? '',
      orderNumber: orderNumber ?? '',
      poNumber: singleOrderDetail.poNumber ?? '',
    }

    const { specifications, laborResults } = sellerCartData.vehicles[0]

    this.performTransfer(transferPB, specifications, laborResults)
    store.dispatch(
      cartSlice.actions.removeAllVehiclesFromCart(GaTrackOption.doNotTrack)
    )
  }

  private performTransfer(
    partsBasket: IPartsBasket12 | IPartsBasket11,
    specifications: VehicleSpecificationItem[] = [],
    laborData: LaborItem[] = []
  ): void {
    if (this.detectedPartsBasketVersion === '1.0') {
      this.partnerCommunicationManager?.cartCallBack(
        JSON.stringify({
          part: [
            ...(partsBasket as IPartsBasket12).lineInfo.line.map((line) => ({
              ...line,
              status: 1,
            })),
            ...(laborData || []).map(
              PartsBasketConverter.get10LaborPartFromLaborItem
            ),
          ],
          responseType:
            partsBasketResponseType[(partsBasket as IPartsBasket12).action],
          searchQualifier: {
            ...(this.incomingPartsBasket as IPartsBasketV10).searchQualifier,
            vehicleQualifier: partsBasket.vehicleInfo,
          },
        })
      )
    } else {
      const updatedPartsBasket = {
        ...partsBasket,
        lineInfo: partsBasket.lineInfo || {
          line: [],
        },
      }
      if (laborData.length > 0) {
        updatedPartsBasket.lineInfo.line = [
          ...updatedPartsBasket.lineInfo.line,
          ...laborData.map(PartsBasketConverter.getLaborPartFromLaborItem),
        ]
      }

      if (specifications.length > 0) {
        updatedPartsBasket.lineInfo.line = [
          ...updatedPartsBasket.lineInfo.line,
          ...specifications.map(
            PartsBasketConverter.getSpecificationfromSpecificationItem
          ),
        ]
      }

      this.partnerCommunicationManager?.cartCallBack(
        JSON.stringify(updatedPartsBasket)
      )
    }
  }

  private static mapSellerCartToPartsBasket(
    sellers: SellerWithLocations[]
  ): Array<IPartsBasketLine11> {
    const mapped: IPartsBasketLine11[] = []

    sellers.forEach((seller) => {
      const isMyPlaceStore: boolean = seller.coverageKey === ''

      seller.locations.forEach((location) => {
        location.parts.forEach((cartPart: CartProduct) => {
          const partLocation = cartPart.location.find(
            (loc) =>
              loc.locationId.toString() === location.locationId.toString()
          )
          if (!partLocation) return

          const totalBundleQty = cartPart.bundleInfo.reduce(
            (sum, bundle) => sum + bundle.quantityRequested,
            0
          )
          const totalBundlePrice = cartPart.bundleInfo.reduce(
            (sum, bundle) =>
              sum + (bundle.bundlePrice || 0) * bundle.quantityRequested,
            0
          )
          const avgPricePerQty =
            totalBundleQty > 0
              ? totalBundlePrice / totalBundleQty
              : partLocation.cost

          const line: IPartsBasketLine11 = {
            seqNo: 1,
            status: cartPart.status || '',
            type: cartPart.type,
            brand: cartPart.brand || '',
            description: cartPart.description,
            lineNo: cartPart.lineNo,
            locationDescription: !isMyPlaceStore
              ? seller.sellerName
              : partLocation.called,
            locationId: !isMyPlaceStore
              ? seller.coverageKey
              : partLocation.locationId,
            isMultiSeller: !isMyPlaceStore,
            manufacturerCode: cartPart.lineCode,
            partCategory: cartPart.catLineCode,
            partNumber: cartPart.partNumber,
            priceOverride: false,
            priceOverrideMsg: '',
            quantityRequested: cartPart.quantityRequested,
            quantityAvailable: partLocation.qtyAvailable,
            unitCorePrice: partLocation.coreCost,
            unitCostPrice: avgPricePerQty,
            unitListPrice: partLocation.list,
            unitListCore: partLocation.coreList,
            unitOfMeasure: partLocation.unitOfMeasure,
          }
          mapped.push(line)
        })
      })
    })

    return mapped
  }

  private prepareSellerBasketForTransfer(
    lines: IPartsBasketLine11[],
    action: PartsBasketAction,
    vehicle: Vehicle
  ): IPartsBasket12 | IPartsBasket11 {
    let partsBasket: IPartsBasket12 | IPartsBasket11 = {
      version: this.detectedPartsBasketVersion,
      uid: (this.incomingPartsBasket as IPartsBasket11)?.uid,
      action,
      lineInfo: {
        line: lines,
      },
      vehicleInfo: {
        ymmeNo: getYMMENumberFromVehicle(vehicle),
        ymme: formatVehicleDisplay(vehicle),
        licencePlate: vehicle.plate,
        vin: vehicle.vin,
      },
    }
    partsBasket = PartsBasketConverter.setAction(partsBasket, action)
    return partsBasket
  }

  public transferSellerCart(): void {
    const { vehicle, specifications, laborResults, sellers } =
      store.getState().cart.cartData.vehicles[0]

    const lines = EmbeddedCommunications.mapSellerCartToPartsBasket(sellers)

    const transferPB = this.prepareSellerBasketForTransfer(
      lines,
      PartsBasketAction.goShoppingResponse,
      vehicle
    )
    this.performTransfer(transferPB, specifications, laborResults)
    store.dispatch(
      cartSlice.actions.removeAllVehiclesFromCart(GaTrackOption.doNotTrack)
    )
  }

  // TODO: We need to look into sending the proper `searchQualifier`.
  public getLocalInventory(
    results: SearchResultsResponse
  ): Promise<ProductModel[]> {
    return new Promise((resolve) => {
      this.partnerCommunicationManager?.getLocalInventory(
        searchResultsToPartsBasket(results),
        results.parts || [],
        resolve
      )
    })
  }

  private static findPayload = (partsBasketStr: string) => {
    try {
      // For version 1.2, the parts basket is nested under partsBasket.partsBasket. Not confusing at all.
      const partsBasketMaybe = JSON.parse(partsBasketStr)
      const partsBasket = partsBasketMaybe.partsBasket
        ? partsBasketMaybe.partsBasket
        : partsBasketMaybe

      return partsBasket
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      return {}
    }
  }

  public static handleCartOnlyMessage = (
    message: CheckAvailabilityResponse11 | CheckAvailabilityResponse12
  ): void => {
    const messageTo12 = 'Data' in message ? message.Data : message
    const cart = CartOnlyCommunications.availabilityResponseToCart(messageTo12)
    store.dispatch(cartSlice.actions.setCartData(cart))
    Config.setCartOnlyMode()
  }

  public cancelCallBack(): void {
    this.partnerCommunicationManager?.cancelCallBack()
  }
}

export const EmbeddedCommunicationsManager = new EmbeddedCommunications()
